import {defineStore} from 'pinia'
import {IOptionPrivate, IProductPrivate} from "@/interface/services/response/product.interface"

import productService from '@/services/productService'
import {useAuthenticationStore} from './authentication'
import {useCustomerStore} from './customer'
import {useContractStore} from './contract'
import { IDescription } from '@/interface'

export const useProductStore = defineStore('product', {
    state: (): IProductState => ({
        baseProduct: null,
        enabledOptions: null,
        lastUpdate: null,
        totalVolumeOptions: null
    }),
    actions: {
        async initProduct(id: number, options: number[]|null) {
            const allIds = [id, ...options??[]]
            const customerStore = useCustomerStore()
            const contractStore = useContractStore()
            const response = await productService.getContractProducts(allIds, customerStore.id, contractStore.currentId ?? 0)
            if (response) {
                this.baseProduct = response?.find(e => e.type === 'BASE') ?? null
                this.enabledOptions = []
                if (options && options.length > 0) {

                    const currentActiveOption: {[key: number]: number} = {}
                    options.forEach((id) => currentActiveOption[id] = currentActiveOption[id] ? currentActiveOption[id] + 1 : 1);
                    const allOptions = options.map(option => {
                        return response?.filter((e) => {
                            e.currentActiveOption = currentActiveOption[e.id];
                            return e.type === 'OPTION' && option === e.id
                        })
                    });

                    this.enabledOptions =  allOptions.flat().sort((a, b) => b.pricing.price - a.pricing.price) ?? [];
                    this.totalVolumeOptions = this.volumeOptions;
                }
            } else {
                this.baseProduct = null
                this.enabledOptions = []
                this.totalVolumeOptions = null
            }

            this.lastUpdate = new Date()            
        }
    },
    getters: {
        id(state: IProductState) : number {
            return state.baseProduct?.id ?? 0
        },
        name(state: IProductState) : string|null {
            return state.baseProduct?.name ?? null
        },
        descriptions(state: IProductState): IDescription {
            return state.baseProduct?.descriptions ?? { fr: '', en: '', nl: '' }
        },
        price(state: IProductState) {
            return state.baseProduct?.pricing.price || 0
        },
        priceOptions(state: IProductState) {
            if (!state.enabledOptions) {
                return 0
            }
            
            let price = 0
            state.enabledOptions?.forEach(opt => {
                price += opt.pricing?.price || 0
            })

            return price
        },
        volume(state: IProductState) {
            return {
                voice: state.baseProduct?.voice.limit_amount ?? 0,
                data: state.baseProduct?.data.limit_amount ?? 0,
                sms: state.baseProduct?.sms.limit_amount ?? 0
            }
        },
        volumeOptions(state: IProductState) {
            let voice = 0
            state.enabledOptions?.forEach(opt => {
                voice += opt.voice?.limit_amount ?? 0
            })

            let data = 0
            state.enabledOptions?.forEach(opt => {
                data += opt.data?.limit_amount ?? 0
            })

            let sms = 0
            state.enabledOptions?.forEach(opt => {
                sms += opt.sms?.limit_amount ?? 0
            })

            return {
                voice: voice,
                data: data,
                sms: sms
            };
        },
        totalVolume(state: IProductState) {
            return {
                voice: (state.baseProduct?.voice.limit_amount ?? 0) + (state.totalVolumeOptions?.voice ?? 0),
                data: ((state.baseProduct?.data.limit_amount ?? 0) + (state.totalVolumeOptions?.data ?? 0)) * 1024,
                sms: (state.baseProduct?.sms.limit_amount ?? 0) + (state.totalVolumeOptions?.sms ?? 0)
            }
        },
        enabledOptionsUniqueId(state: IProductState) {
            if (!state.enabledOptions) {
                return [];
            }
            const objectsUniques: {[key: number]: IOptionPrivate} = {};
            state.enabledOptions.forEach((object) => objectsUniques[object.id] = object);
            return Object.values(objectsUniques).sort((a, b) => a.pricing.price - b.pricing.price);
        }
    },
    persist: true
})

interface IProductState {
    baseProduct: IProductPrivate|null,
    enabledOptions: IOptionPrivate[]|null
    lastUpdate: Date|null,
    totalVolumeOptions: volume|null
}

interface volume {
    voice: number,
    data: number,
    sms: number
}

export type ProductStore = ReturnType<typeof useProductStore>